import { TaskPriority, TaskStatus } from '../types/Task';

export const MAX_TITLE_LENGTH = 100;
export const MAX_DESCRIPTION_LENGTH = 500;
export const MAX_LABELS = 10;
export const MAX_LABEL_LENGTH = 30;
export const MAX_CHECKLIST_ITEM_LENGTH = 200;
export const MAX_FUTURE_DUE_DATE_DAYS = 365;
export const MAX_ATTACHMENT_FILES = 3;
export const MAX_ATTACHMENT_FILE_SIZE_BYTES = 1024 * 1024;
export const MAX_ATTACHMENT_FILE_SIZE_LABEL = '1 MB';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const DUE_DATE_PRESET_OPTIONS = [
  { key: 'today', label: 'Today', daysFromToday: 0 },
  { key: 'tomorrow', label: 'Tomorrow', daysFromToday: 1 },
  { key: 'next-week', label: 'Next Week', daysFromToday: 7 },
] as const;

export interface ChecklistFormItem {
  text: string;
  done: boolean;
}

export interface TaskFormValues {
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string;
  labels: string;
  checklistItems: ChecklistFormItem[];
}

export const normalizeTaskText = (value: string) => value.trim();

export const createEmptyChecklistItem = (): ChecklistFormItem => ({
  text: '',
  done: false,
});

const formatLocalDateString = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseLocalDateString = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const parseLabelsInput = (value: string): string[] => (
  value
    .split(',')
    .map((label) => label.trim())
    .filter(Boolean)
);

const hasDuplicateLabels = (labels: string[]) => {
  const seen = new Set<string>();

  return labels.some((label) => {
    const normalizedLabel = label.toLocaleLowerCase();
    if (seen.has(normalizedLabel)) {
      return true;
    }

    seen.add(normalizedLabel);
    return false;
  });
};

export const getTodayDateString = () => {
  const now = new Date();
  return formatLocalDateString(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
};

const getDueDateOffsetFromToday = (dueDate: string) => {
  if (!dueDate) {
    return null;
  }

  const today = parseLocalDateString(getTodayDateString());
  const targetDate = parseLocalDateString(dueDate);
  return Math.round((targetDate.getTime() - today.getTime()) / MS_PER_DAY);
};

export const getDueDatePresetValue = (daysFromToday: number) => {
  const presetDate = parseLocalDateString(getTodayDateString());
  presetDate.setDate(presetDate.getDate() + daysFromToday);
  return formatLocalDateString(presetDate);
};

export const validateTaskForm = (formData: TaskFormValues) => {
  const newErrors: Record<string, string> = {};
  const normalizedTitle = normalizeTaskText(formData.title);
  const normalizedDescription = normalizeTaskText(formData.description);
  const dueDateOffset = getDueDateOffsetFromToday(formData.dueDate);

  if (!normalizedTitle) {
    newErrors.title = 'Title is required';
  } else if (formData.title.length > MAX_TITLE_LENGTH) {
    newErrors.title = `Title must not exceed ${MAX_TITLE_LENGTH} characters`;
  }

  if (formData.description.length > MAX_DESCRIPTION_LENGTH) {
    newErrors.description = `Description must not exceed ${MAX_DESCRIPTION_LENGTH} characters`;
  } else if (normalizedDescription && normalizedTitle === normalizedDescription) {
    newErrors.description = 'Description must differ from title';
  }

  const parsedLabels = parseLabelsInput(formData.labels);
  const tooLongLabel = parsedLabels.find((label) => label.length > MAX_LABEL_LENGTH);

  if (parsedLabels.length > MAX_LABELS) {
    newErrors.labels = `Use at most ${MAX_LABELS} labels`;
  } else if (tooLongLabel) {
    newErrors.labels = `Each label must be ${MAX_LABEL_LENGTH} characters or fewer`;
  } else if (hasDuplicateLabels(parsedLabels)) {
    newErrors.labels = 'Duplicate labels are not allowed';
  }

  if (formData.priority === TaskPriority.URGENT && !formData.dueDate) {
    newErrors.dueDate = 'Urgent tasks require a due date';
  } else if (
    formData.priority === TaskPriority.URGENT
    && dueDateOffset !== null
    && dueDateOffset < 0
  ) {
    newErrors.dueDate = 'Urgent tasks cannot use a past due date';
  } else if (dueDateOffset !== null && dueDateOffset > MAX_FUTURE_DUE_DATE_DAYS) {
    newErrors.dueDate = `Due date must be within the next ${MAX_FUTURE_DUE_DATE_DAYS} days`;
  }

  const emptyChecklistItem = formData.checklistItems.find((item) => !normalizeTaskText(item.text));
  const oversizedChecklistItem = formData.checklistItems.find(
    (item) => normalizeTaskText(item.text).length > MAX_CHECKLIST_ITEM_LENGTH
  );

  if (emptyChecklistItem) {
    newErrors.checklistItems = 'Checklist item text is required';
  } else if (oversizedChecklistItem) {
    newErrors.checklistItems = `Checklist items must be ${MAX_CHECKLIST_ITEM_LENGTH} characters or fewer`;
  }

  return newErrors;
};

export const getDueDateWarning = (dueDate: string, priority: TaskPriority) => {
  if (!dueDate) {
    return null;
  }

  return priority !== TaskPriority.URGENT && dueDate < getTodayDateString()
    ? 'Due date is in the past. You can still save this task.'
    : null;
};

export const buildTaskPayload = (formData: TaskFormValues) => ({
  ...formData,
  title: normalizeTaskText(formData.title),
  description: normalizeTaskText(formData.description),
  dueDate: formData.dueDate || undefined,
  labels: parseLabelsInput(formData.labels),
  checklistItems: formData.checklistItems.map((item) => ({
    text: normalizeTaskText(item.text),
    done: item.done,
  })),
});